export interface HistoryMessage {
  kind: 'message';
  role: 'user' | 'assistant';
  content: string;
  run_id?: string;
  seq?: number;
}

export interface HistoryTool {
  kind: 'tool';
  id: string;
  tool: string;
  args: Record<string, unknown>;
  state: 'running' | 'ok' | 'error';
  summary?: string;
  run_id?: string;
}

export type HistoryItem = HistoryMessage | HistoryTool;

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function jsonValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
}

export function parseHistoryMessages(value: unknown): HistoryItem[] {
  if (!Array.isArray(value)) return [];
  const items: HistoryItem[] = [];
  const toolIndexes = new Map<string, number>();
  for (const raw of value) {
    const row = objectValue(raw);
    if (!row) continue;
    const role = String(row.role || '');
    const runId = String(row.run_id || '');
    const seq = Number.isFinite(Number(row.seq)) ? Number(row.seq) : undefined;
    if (role === 'user') {
      const content = typeof row.content === 'string' ? row.content : String(row.content || '');
      if (content.trim()) items.push({ kind: 'message', role: 'user', content, run_id: runId, ...(seq === undefined ? {} : { seq }) });
      continue;
    }
    if (role === 'assistant') {
      const parsed = jsonValue(row.content);
      if (!Array.isArray(parsed)) {
        const content = typeof parsed === 'string' ? parsed : '';
        if (content.trim()) items.push({ kind: 'message', role: 'assistant', content, run_id: runId, ...(seq === undefined ? {} : { seq }) });
        continue;
      }
      const text: string[] = [];
      for (const rawBlock of parsed) {
        const block = objectValue(rawBlock);
        if (!block) continue;
        if (block.type === 'text' && typeof block.text === 'string') text.push(block.text);
        if (block.type === 'tool_use') {
          const id = String(block.id || `history-tool-${items.length}`);
          const entry: HistoryTool = {
            kind: 'tool', id, tool: String(block.name || 'tool'), args: objectValue(block.input) || {}, state: 'running', run_id: runId,
          };
          toolIndexes.set(id, items.length);
          items.push(entry);
        }
      }
      const content = text.join('');
      if (content.trim()) items.push({ kind: 'message', role: 'assistant', content, run_id: runId, ...(seq === undefined ? {} : { seq }) });
      continue;
    }
    if (role === 'tool_result') {
      const resultBlock = objectValue(jsonValue(row.content));
      if (!resultBlock) continue;
      const id = String(resultBlock.tool_use_id || '');
      const result = objectValue(resultBlock.result);
      const index = toolIndexes.get(id);
      const state = result?.ok === false ? 'error' : 'ok';
      const summary = typeof result?.summary === 'string' ? result.summary : undefined;
      if (index !== undefined) {
        items[index] = { ...(items[index] as HistoryTool), state, summary };
      } else if (id) {
        items.push({ kind: 'tool', id, tool: String(resultBlock.name || 'tool'), args: {}, state, summary, run_id: runId });
      }
    }
  }
  return items;
}
